import { CouponInput } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { PauseInputDto } from "@modules/common/pauseObject/gateway/pauseApiDto"
import { ApiClient } from "@modules/core/api/client/apiClient"
import { ProgramSubscriberApiGateway } from "@modules/programs/programSubscriber/gateway/programSubscriberApiGateway"
import {
  ProgramSubscriberDto,
  ProgramSubscriberDtoSchema,
} from "@modules/programs/programSubscriber/gateway/programSubscriberDto"
import { Paginated, PaginatedWrapper, toPage, wrapperToPage } from "@modules/utils/paginated"

export class ProgramSubscriberApi implements ProgramSubscriberApiGateway {
  private baseUrl = "/programs"

  constructor(private api: ApiClient) {}

  async getProgramSubscriber(programId: string, subscriberId: string) {
    const useNewEndpoint = Math.random() < 0.5
    const result = useNewEndpoint
      ? await this.api.get<ProgramSubscriberDto>(`${this.baseUrl}/${programId}/subscribers/new/${subscriberId}`)
      : await this.api.get<ProgramSubscriberDto>(`${this.baseUrl}/${programId}/subscribers/${subscriberId}`)
    return ProgramSubscriberDtoSchema.parse(result.data)
  }

  async getProgramSubscribers(programId: string, page = 0, size = 30) {
    const result = await this.api.get<Paginated<ProgramSubscriberDto>>(`${this.baseUrl}/${programId}/subscribers`, {
      params: {
        page,
        size,
      },
    })
    const pageResult = toPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content.map((subscriber) => ProgramSubscriberDtoSchema.parse(subscriber)),
    }
  }

  async searchProgramSubscribers(programId: string, page = 0, size = 30, searchTerm?: string) {
    const result = await this.api.get<PaginatedWrapper<ProgramSubscriberDto>>(
      `${this.baseUrl}/${programId}/subscribers/search`,
      {
        params: {
          nameOrUsername: searchTerm,
          page,
          size,
        },
      },
    )
    const pageResult = wrapperToPage(result.data)

    return {
      ...pageResult,
      content: pageResult.content.map((subscriber) => ProgramSubscriberDtoSchema.parse(subscriber)),
    }
  }

  async updateSubscriberPause(programId: string, subscriberId: string, pause: PauseInputDto) {
    await this.api.post(`${this.baseUrl}/${programId}/subscribers/${subscriberId}/pause`, pause)
  }

  async resumeSubscription(programId: string, subscriberId: string) {
    await this.api.post(`${this.baseUrl}/${programId}/subscribers/${subscriberId}/resume`)
  }

  async updateSubscriberInternalNote(programId: string, subscriberId: string, note: string) {
    const result = await this.api.post(`${this.baseUrl}/${programId}/subscribers/${subscriberId}/note`, {
      note,
    })

    return ProgramSubscriberDtoSchema.parse(result.data)
  }

  async removeSubscriber(programId: string, subscriberId: string, cancelAtPeriodEnd: boolean) {
    await this.api.delete(`${this.baseUrl}/${programId}/subscribers/${subscriberId}`, {
      data: {
        cancelAtPeriodEnd,
      },
    })
  }

  async removeSubscriptionDiscount(programId: string, subscriberId: string) {
    await this.api.delete(`${this.baseUrl}/${programId}/subscribers/${subscriberId}/coupons`)
  }

  async applySubscriptionDiscount(programId: string, subscriberId: string, coupon: CouponInput) {
    await this.api.post(`${this.baseUrl}/${programId}/subscribers/${subscriberId}/coupons`, coupon)
  }
}
